import { useEffect, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import type { Account, Category, CreditCard, Invoice, Transaction } from '@domain/finance'
import { chaveDeDiscussao, grupoDeParcelas, rotuloDaParcela } from '@domain/finance'
import { formatBRL } from '@shared/money/format'
import { sumCents } from '@shared/money/cents'
import { currentReferenceMonth } from '@shared/date/timezone'
import { Glass } from '@ui/glass/Glass'
import { Numero as NumeroDeDisplay } from '@ui/glass/Numero'
import { BreakdownSection } from '@ui/finance/BreakdownSection'
import { CommentThread } from '@ui/finance/CommentThread'
import { SeletorDeCategoria } from '@ui/finance/SeletorDeCategoria'
import { SeloPendente } from '@ui/finance/SeloPendente'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { fullDayLabel } from '@ui/finance/day-groups'
import { useTransaction } from '@ui/finance/use-transaction'
import { NotaDaTransacao } from '@ui/notas/NotaDaTransacao'
import { SecaoAbatimento } from '@ui/finance/abatimento/SecaoAbatimento'
import { Voltar } from '@ui/shell/Voltar'

/**
 * O detalhe de uma transação. `Docs/04-Design/Telas/Detalhe da transação.md`.
 *
 * *"Onde a transação vira conhecimento. Tela empilhada, não aba."*
 *
 * ## `source` é somente leitura, e a tela EXPLICA em vez de bloquear
 *
 * A nota é literal sobre isso: *"Se o usuário tocar num campo de origem, a
 * resposta é uma explicação, não um input"*. Um campo desabilitado convida a
 * tentar e não diz por quê; uma frase encerra a dúvida.
 * [[ADR-0005 - Dado original imutável]].
 *
 * ## A terceira data não existe na compra de cartão, e a tela diz o que existe
 *
 * `settlementDate` é `null` **para sempre** numa compra de cartão (decisão 94) —
 * quem entra no caixa é o pagamento da fatura. Em vez de um campo vazio, a tela
 * aponta para a fatura: *"entra na fatura de julho, que vence 10/08"*. Numa
 * compra `direct` as três datas coincidem e não há o que explicar.
 *
 * ## O que ainda NÃO está aqui
 *
 * A nota fiscal é da Fase 8, e não aparece como slot vazio: um lugar reservado
 * para o que não existe promete uma coisa que a tela não pode cumprir.
 */

/** `'2026-07-15'` → `'15/07/2026'`. */
function fullDate(date: string): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`
}

function Fato({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-[var(--text-muted)]">{rotulo}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  )
}

/** "À vista", ou "3 de 12". A palavra é da tela; o fato vem do domínio. */
function parcela(transaction: Transaction): string {
  return rotuloDaParcela(transaction) ?? 'À vista'
}

/**
 * A frase que substitui a data de caixa ausente numa compra de cartão.
 *
 * Sem a fatura carregada ela não aparece — dizer "entra numa fatura" sem dizer
 * qual não explica nada, e a fatura chega logo depois.
 */
function Fatura({ invoice }: { invoice: Invoice | null }) {
  if (invoice === null) return null
  return (
    <p className="mt-3 text-sm text-[var(--text-muted)]">
      {`Entra na fatura de ${invoice.referenceMonth}, que vence ${fullDate(invoice.dueDate)}. ` +
        'O dinheiro sai da conta quando essa fatura é paga.'}
    </p>
  )
}

/**
 * O nome do instrumento. **Pelo nome, não pelo tipo**: a primeira versão
 * devolvia a string `'Conta'`, e a tela ficava com o rótulo "Conta" em cima do
 * valor "Conta" — duas palavras iguais que não dizem nada, e um `getByText`
 * ambíguo no teste foi quem apontou.
 *
 * Conta e cartão apagados dizem que sumiram: o `instrument.id` cru não ajudaria
 * ninguém, e a transação continua existindo.
 */
function instrumentoDe(
  transaction: Transaction,
  creditCards: CreditCard[],
  accounts: Account[],
): string {
  const { instrument } = transaction.source
  return instrument.type === 'creditCard'
    ? (creditCards.find((c) => c.id === instrument.id)?.name ?? 'cartão que não existe mais')
    : (accounts.find((a) => a.id === instrument.id)?.name ?? 'conta que não existe mais')
}

/** Categoria e observação: as duas coisas que a tela edita. */
function Enriquecimento({
  transaction,
  categories,
  onPatch,
  onCategoriasMudaram,
}: {
  transaction: Transaction
  categories: Category[]
  onPatch: (patch: { categoryId?: string | null; note?: string | null }) => Promise<void>
  onCategoriasMudaram: () => void
}) {
  const [nota, setNota] = useState(transaction.enrichment.note ?? '')
  const [falhou, setFalhou] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const gravar = (patch: { categoryId?: string | null; note?: string | null }) => {
    setSalvando(true)
    setFalhou(false)
    onPatch(patch)
      .catch(() => {
        setFalhou(true)
      })
      .finally(() => {
        setSalvando(false)
      })
  }

  // Sem perguntar, mas dizendo. Trocar a categoria de 1/4 muda outras três
  // linhas, e mudar três linhas em silêncio é o tipo de coisa que assusta na
  // segunda vez — a nota do detalhe pede explicação, não bloqueio.
  const parcelas = transaction.source.installment
  const daCompraInteira =
    grupoDeParcelas(transaction) === null || parcelas === null
      ? null
      : `Vale para as ${String(parcelas.total)} parcelas desta compra.`

  return (
    <>
      <Fato rotulo="Categoria">
        {transaction.enrichment.offsetOf !== null ? (
          // A categoria de uma entrada ligada não conta em soma nenhuma — ela
          // segue o gasto que abate. Um seletor aqui seria enganar.
          <span className="text-[var(--text-muted)]">segue o gasto</span>
        ) : (
          <>
            <SeletorDeCategoria
              categorias={categories}
              value={transaction.enrichment.categoryId}
              rotulo="Categoria"
              disabled={salvando}
              onChange={(categoryId) => {
                gravar({ categoryId })
              }}
              onCriou={onCategoriasMudaram}
            />
            {daCompraInteira !== null && (
              <span className="mt-0.5 block text-xs text-[var(--text-muted)]">
                {daCompraInteira}
              </span>
            )}
          </>
        )}
      </Fato>

      <div className="col-span-2">
        <label className="text-xs text-[var(--text-muted)]" htmlFor="observacao">
          Observação
        </label>
        {/* **Grava no `blur`, não a cada tecla.** Uma escrita por caractere
            digitado seria dezenas de round-trips para uma frase — e o campo é
            texto livre, sem nada que precise de validação em tempo real. */}
        <textarea
          id="observacao"
          rows={2}
          value={nota}
          className="mt-1 w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1 text-sm"
          onChange={(event) => {
            setNota(event.target.value)
          }}
          onBlur={() => {
            const limpa = nota.trim()
            const atual = transaction.enrichment.note ?? ''
            // Nada mudou, nada é gravado. Sem isto, abrir e sair do campo
            // marcaria a transação como editada.
            if (limpa === atual) return
            // Vazio é `null`, e não string vazia: "não escreveu nada" e
            // "escreveu e apagou" são a mesma coisa para quem lê depois.
            gravar({ note: limpa === '' ? null : limpa })
          }}
        />
      </div>

      {falhou && (
        <p role="alert" className="col-span-2 text-sm text-[var(--text-muted)]">
          Não foi possível salvar agora.
        </p>
      )}
    </>
  )
}

/**
 * As outras parcelas da mesma compra, com a de agora marcada.
 *
 * ## Uma leitura própria, e que não bloqueia a tela
 *
 * `useTransaction` carrega a transação e o que a explica; as irmãs são outra
 * pergunta, e a tela não deve esperar por elas — a mesma escolha da fatura,
 * que "chega quando chegar". Por isso um componente com a leitura dele, e não
 * um quinto item no `Promise.all` do detalhe.
 *
 * ## Grupo incompleto acontece, e a soma não se chama "total" nele
 *
 * Medido: 16 das 124 compras da casa têm parcela faltando — antigas fora da
 * janela de sync, ou que ainda vão chegar. Somar as que
 * chegaram e escrever "Total da compra" poria na tela um número de dinheiro
 * errado com cara de certo. Incompleto, o título diz quantas chegaram, e a
 * soma diz que é das que chegaram.
 *
 * ## A cancelada não entra
 *
 * `canceled` é a lápide de quando o banco apaga a linha. Contá-la somaria uma
 * parcela que não existe mais.
 *
 * ## Sem o mês da fatura de cada parcela, por enquanto
 *
 * Ficou de fora porque, numa compra de 10x desta casa, as parcelas 3 a 10
 * aparecem todas na mesma fatura, e isso parecia defeito da ingestão (decisão
 * 279). **Não é** (decisão 280): foram parcelas antecipadas, e o banco as põe
 * na fatura corrente. O mês pode entrar — e, mostrado, ele deixa a
 * antecipação visível. Ficou registrado como proposta.
 */
function ParcelasDaCompra({ transaction }: { transaction: Transaction }) {
  const { transactionRepository } = useFinanceRepositories()
  const grupo = grupoDeParcelas(transaction)
  const [parcelas, setParcelas] = useState<Transaction[] | 'carregando' | 'falhou'>('carregando')

  useEffect(() => {
    if (grupo === null) return
    let vivo = true
    transactionRepository
      .listByInstallmentGroup(grupo)
      .then((lidas) => {
        if (vivo) setParcelas(lidas)
      })
      .catch((erro: unknown) => {
        console.error('ParcelasDaCompra: a leitura das parcelas falhou', erro)
        if (vivo) setParcelas('falhou')
      })
    return () => {
      vivo = false
    }
  }, [transactionRepository, grupo])

  const total = transaction.source.installment?.total ?? null
  if (grupo === null || total === null) return null

  if (parcelas === 'carregando') {
    return (
      <p role="status" className="mt-4 text-sm text-[var(--text-muted)]">
        Carregando as parcelas…
      </p>
    )
  }
  if (parcelas === 'falhou') {
    return (
      <p role="alert" className="mt-4 text-sm text-[var(--text-muted)]">
        Não foi possível carregar as outras parcelas agora.
      </p>
    )
  }

  const vivas = parcelas
    .filter((p) => p.status !== 'canceled')
    .sort((a, b) => (a.source.installment?.number ?? 0) - (b.source.installment?.number ?? 0))
  const completa = vivas.length >= total
  const soma = sumCents(vivas.map((p) => p.source.amountCents))

  return (
    <section aria-label="Parcelas da compra" className="mt-4 border-t border-[var(--border)] pt-3">
      <h2 className="mb-1 text-xs text-[var(--text-muted)]">
        {completa
          ? `Parcelas da compra (${String(total)})`
          : `Parcelas da compra — chegaram ${String(vivas.length)} de ${String(total)}`}
      </h2>
      <ul>
        {vivas.map((p) => {
          const esta = p.id === transaction.id
          const conteudo = (
            <>
              <span className="flex-1">
                {rotuloDaParcela(p)}
                {esta && <span className="ml-2 text-xs text-[var(--text-muted)]">esta</span>}
              </span>
              <SeloPendente status={p.status} />
              <span className="tabular-nums">{formatBRL(p.source.amountCents)}</span>
            </>
          )
          return (
            <li key={p.id} className="border-b border-[var(--border)] last:border-b-0">
              {esta ? (
                // A de agora não é link: levaria para a própria tela. Marcada
                // com `aria-current`, que é o que um leitor de tela anuncia
                // como "a página atual".
                <div aria-current="page" className="flex min-h-11 items-center gap-2 text-sm">
                  {conteudo}
                </div>
              ) : (
                <Link
                  to="/transacoes/$id"
                  params={{ id: p.id }}
                  className="flex min-h-11 w-full items-center gap-2 text-sm"
                >
                  {conteudo}
                </Link>
              )}
            </li>
          )
        })}
      </ul>
      <p className="mt-2 flex justify-between text-sm">
        <span className="text-[var(--text-muted)]">
          {completa ? 'Total da compra' : `Soma das ${String(vivas.length)} que chegaram`}
        </span>
        <span className="tabular-nums">{formatBRL(soma)}</span>
      </p>
    </section>
  )
}

export function Transacao() {
  const { id } = useParams({ strict: false }) as { id: string }
  const detalhe = useTransaction(id)
  const { transactionRepository } = useFinanceRepositories()

  async function gravar(patch: { categoryId?: string | null; note?: string | null }) {
    if (detalhe.status !== 'loaded') return
    await transactionRepository.updateEnrichment(id, patch)
    detalhe.patchEnrichment(patch)
  }

  return (
    <section>
      <div className="mb-2">
        <Voltar para="/transacoes">Transações</Voltar>
      </div>
      <header className="mb-4">
        <h1 className="titulo text-2xl">Detalhes da transação</h1>
      </header>

      {detalhe.status === 'loading' && (
        <Glass level="frost" className="p-5">
          <p role="status" className="text-sm text-[var(--text-muted)]">
            Carregando…
          </p>
        </Glass>
      )}

      {detalhe.status === 'notFound' && (
        <Glass level="frost" className="p-5">
          {/* Não é `alert`: nada deu errado. Um link antigo é o caso normal de
              uma URL feita para ser compartilhada. */}
          <p className="text-sm text-[var(--text-muted)]">Esta transação não existe mais.</p>
        </Glass>
      )}

      {detalhe.status === 'failed' && (
        <Glass level="frost" className="p-5">
          <p role="alert" className="text-sm text-[var(--text-muted)]">
            Não foi possível carregar a transação agora.
          </p>
        </Glass>
      )}

      {detalhe.status === 'loaded' && (
        <Glass level="frost" className="p-5">
          <p className="text-xs text-[var(--text-muted)]">
            {detalhe.transaction.source.description}
          </p>
          <p className="mb-4">
            <NumeroDeDisplay
              tom={detalhe.transaction.source.amountCents < 0 ? 'negativo' : 'positivo'}
            >
              {formatBRL(detalhe.transaction.source.amountCents)}
            </NumeroDeDisplay>
          </p>

          <dl className="grid grid-cols-2 gap-3">
            <Fato rotulo="Data da compra">
              {fullDate(detalhe.transaction.source.transactionDate)}
            </Fato>
            <Fato rotulo="Data do lançamento">
              {detalhe.transaction.source.postedAt === null
                ? '—'
                : fullDate(detalhe.transaction.source.postedAt)}
            </Fato>
            <Fato rotulo={detalhe.transaction.source.instrument.type === 'creditCard' ? 'Cartão' : 'Conta'}>
              {instrumentoDe(detalhe.transaction, detalhe.creditCards, detalhe.accounts)}
            </Fato>
            <Fato rotulo="Parcela">{parcela(detalhe.transaction)}</Fato>
            {detalhe.transaction.status === 'pending' && (
              // Aqui há espaço para a frase inteira, e é aqui que se decide
              // vincular uma nota — a tela onde saber que o valor ainda pode
              // mudar muda a decisão.
              <div className="col-span-2">
                <span className="text-xs" style={{ color: 'var(--pendente)' }}>
                  Compra pendente
                </span>
                <p className="text-xs text-[var(--text-muted)]">
                  O banco ainda não fechou esta compra. O valor e a descrição podem mudar até a
                  fatura fechar — gorjeta de restaurante e ajuste de posto são os casos comuns.
                  Dá para vincular uma nota assim mesmo.
                </p>
              </div>
            )}
            {detalhe.transaction.settlementDate !== null && (
              <Fato rotulo="Saiu da conta em">
                {fullDayLabel(detalhe.transaction.settlementDate)}
              </Fato>
            )}
            <Enriquecimento
              transaction={detalhe.transaction}
              categories={detalhe.categories}
              onPatch={gravar}
              onCategoriasMudaram={detalhe.recarregarCategorias}
            />
          </dl>

          <Fatura invoice={detalhe.invoice} />

          {/* `key` pela compra: trocar de parcela dentro do mesmo grupo não
              relê, e trocar de grupo recomeça do "carregando" em vez de mostrar
              por um instante as parcelas da compra anterior. */}
          <ParcelasDaCompra
            key={grupoDeParcelas(detalhe.transaction) ?? detalhe.transaction.id}
            transaction={detalhe.transaction}
          />

          {/* `key` pela transação: trocar de linha recomeça a leitura em vez
              de mostrar por um instante as entradas da compra anterior. */}
          <SecaoAbatimento
            key={detalhe.transaction.id}
            transaction={detalhe.transaction}
            onVinculo={detalhe.aplicarVinculo}
          />

          <BreakdownSection
            transaction={detalhe.transaction}
            categories={detalhe.categories}
            onCategoriasMudaram={detalhe.recarregarCategorias}
          />

          <NotaDaTransacao
            transaction={detalhe.transaction}
            onCategorizou={(categoryId) => {
              detalhe.patchEnrichment({ categoryId })
            }}
          />

          {/* **Endereçada pela compra, e não pela linha.** Um comentário escrito
              na 3/12 tem de existir para quem abrir a 4/12 — são a mesma ida à
              loja. `chaveDeDiscussao` devolve o `groupId` quando há um, e o id
              da transação quando é à vista; o `entityType` continua
              `'transaction'` porque é de uma transação que se está falando. */}
          <CommentThread
            entityType="transaction"
            entityId={chaveDeDiscussao(detalhe.transaction)}
            anoCorrente={currentReferenceMonth().slice(0, 4)}
          />

          {/* A explicação que a nota pede, sempre visível em vez de aparecer ao
              tocar num campo bloqueado: quem lê a tela entende a regra antes de
              esbarrar nela. */}
          <p className="mt-4 border-t border-[var(--border)] pt-3 text-xs text-[var(--text-muted)]">
            {detalhe.transaction.source.kind === 'pluggy'
              ? 'Descrição, valor e datas vieram do banco e não podem ser alterados. Você pode mudar a categoria e adicionar uma observação.'
              : 'Esta transação foi lançada à mão.'}
          </p>
        </Glass>
      )}
    </section>
  )
}
